import type { ApprovalLineItem, ApprovalSignatureDisplay } from './types';

/**
 * 결재 칸 결합 아이템 — 정의(ApprovalLineItem) base + 서명(ApprovalSignatureDisplay) 오버레이.
 *
 * surface 통일(2026-06-10): 상세(ApprovalDisplayTable)·미리보기·print·공유가 모두
 * 이 함수로 정의 기반 칸을 만들어 동일하게 렌더한다.
 * - approvalLines가 비어있는 레거시 케이스는 approvalSignatures를 그대로 사용(하위호환).
 * - **작성 완료 서명이 채우는 칸**(author 단계 + 위임 담당 칸)은 별도 작성자 칸이 아니라 일반
 *   결재 칸으로 렌더하고, 제출 전(round 서명 없음)에는 authorSignatureData/authorSignerName을
 *   그 칸에 매핑한다({@link fillsFromAuthoringSignature}).
 */
export interface MergedApprovalItem {
  key: string;
  approvalLevel: number;
  label: string;
  name: string;
  signatureData: string | undefined;
  status: 'approved' | 'rejected' | 'pending';
  /**
   * author 단계 칸 — **작성자 서명 모달 클릭 대상**.
   *
   * ⚠️ 「작성 완료 서명이 이 칸을 채우나」와 **다른 축이다**. 그쪽은
   * {@link fillsFromAuthoringSignature}가 판정하며 위임 담당 칸(`delegatedTo`)까지 포함한다.
   * 이 값을 그쪽에 맞춰 넓히면 위임자가 담당 칸에 자기 서명을 넣게 된다(F-25 JSDoc 참조).
   */
  isAuthor: boolean;
  /**
   * P2a: 시뮬레이션 미리보기의 빈 동적 슬롯 표식.
   * true = 작성 시 추가 가능한 빈 칸(layout이 고정 단계와 시각 구분). 실데이터는 undefined.
   */
  placeholder?: boolean;
}

/**
 * 이 칸이 **작성 완료 서명(`report.authorSignature*`)으로 채워지는 칸**인가.
 *
 * 두 종류다. 둘 다 서명의 출처가 `report.authorSignature*` 하나다:
 *  1. `author` — 작성자 본인 칸.
 *  2. `delegatedTo` — **위임 담당 칸.** 위임본의 완료 서명도 같은 컬럼에 저장되고 그 주인은
 *     위임받은 담당자다(외부 완료 경로가 `authorSignerName`에 담당자 이름을 넣는다).
 *
 * ── 서버 preset 판정과 **같은 너비**여야 한다 (F-25, 2026-09-11)
 * 이 술어는 상신 시 서버가 그 칸을 즉시 APPROVED로 확정하는 규칙
 * (`isSlotFilledByAuthoringSignature` — `approval-submit-plan.util`)의 web 거울이다. 더 넓으면
 * **제출 전엔 approved로 그려 놓고 제출 후 PENDING으로 비는** 거짓 표시가 된다.
 *
 * ⚠️ 그래서 shared `isAuthoringSignatureStepDefinitionRow`(`utils/approval-third-party`)를
 * **쓰지 않는다** — 그쪽은 머리 소스(`sourceType ?? assignmentChain[0].type`)로 판정해 서버
 * preset보다 넓다. 구 정의(`sourceType: null` + 체인 `[{author},{role}]`)가 갈라지는 자리다:
 * shared 술어는 true인데 서버 preset ①(`isAuthorStepDefinitionRow`)은 **단일 author 체인만**
 * 인정하므로 채우지 않는다. shared 술어 JSDoc도 그 넓이를 스스로 경고하며 "제3자 결재인가"를
 * 묻는 자리 전용이라 적어 두었다. 여기서는 **해소된 `sourceType`만** 본다 — 서버 ①②의 정확한
 * 거울이고, 해소되지 않은 구 정의(null)는 종전대로 채우지 않는다.
 *
 * ⚠️ 서버 `isSlotFilledByAuthoringSignature`의 ③(**작성 서명의 주인 Member**가 일반 칸 결재자로
 * resolve된 경우 — 직접 작성=작성자, 위임본=담당자 `delegatedContact.memberId`, F-29 2026-09-11)도
 * 여기 없다 — 그 갈래는 **자격 판정**이라 web이 하지 않는다(신원 원료인 owner memberId가 표시
 * 입력에 없다). 제출 후에는 서버가 만든 서명 행이 그대로 오버레이되므로 표시가 갈라지지 않는다.
 *
 * ── 왜 {@link MergedApprovalItem.isAuthor}와 갈라 두는가
 * `isAuthor`는 **「작성자 서명 모달을 여는 클릭 대상」**이라는 다른 축이다(rc StepperLayout ·
 * web ApprovalDisplayTable 두 곳이 `onAuthorSignatureClick`을 그 값으로 건다).
 * 매핑 조건을 넓히자고 `isAuthor`까지 넓히면 **위임자(안전관리자)가 담당 칸을 눌러 자기
 * 서명을 남의 칸에 넣을 수 있게 된다** — 위임본의 담당 칸 서명 주인은 담당자이므로 명의 위조다.
 * 표시(이 술어)와 입력 권한(`isAuthor`)은 끝까지 다른 축으로 둔다.
 */
function fillsFromAuthoringSignature(line: ApprovalLineItem): boolean {
  return line.sourceType === 'author' || line.sourceType === 'delegatedTo';
}

export function buildMergedApprovalItems(
  approvalLines: ApprovalLineItem[],
  approvalSignatures: ApprovalSignatureDisplay[],
  authorSignatureData: string | undefined,
  authorSignerName: string | undefined,
): MergedApprovalItem[] {
  // 레거시 케이스: 정의 없음 + 서명만 있는 구 보고서 — 하위호환 fallback
  if (approvalLines.length === 0 && approvalSignatures.length > 0) {
    return approvalSignatures.map((sig, index) => {
      const stepLabel = sig.stepLabel || `${index + 1}단계`;
      const baseName = sig.signerName || sig.member?.name || '';
      const isDeleted = !!(sig.member?.user?.deletedAt);
      const name = isDeleted ? `${baseName} (탈퇴)` : baseName;
      const stepStatus =
        sig.status === 'APPROVED' ? 'approved' : sig.status === 'REJECTED' ? 'rejected' : 'pending';
      return {
        key: sig.id,
        approvalLevel: sig.approvalLevel,
        label: stepLabel,
        name,
        signatureData: sig.signatureData || undefined,
        status: stepStatus,
        isAuthor: false,
      };
    });
  }

  // 신규 결합 로직: 정의(approvalLines) base + 현재 round 서명 오버레이
  const sigByLevel = new Map(approvalSignatures.map((s) => [s.approvalLevel, s]));

  return approvalLines.map((line, index) => {
    const sig = sigByLevel.get(line.approvalLevel);
    const stepLabel = line.label || sig?.stepLabel || `${index + 1}단계`;
    const isAuthor = line.sourceType === 'author';
    const authoringSlot = fillsFromAuthoringSignature(line);

    // 담당자 미지정 칸(= 종이로 받는 칸)은 이름도 비어 있어야 한다.
    // ⚠️ 아래 fallback을 그대로 타면 **라벨이 사람 이름 자리에 뜨고**("검토" 칸의 결재자
    // 이름이 "검토"), 제출 후에는 서버가 서명행에 채우는 '(담당자)' 폴백이 뜬다. 둘 다
    // 지정된 칸처럼 보여 "빈칸이 실수인지 의도인지"를 지운다.
    // 근거: 결정 2026-08-13 §3 — 출력물에 빈 서명란이 남아야 수기 결재가 가능하다.
    const assigneeUnset = line._assigneeUnset === true;

    // 이름: 정의 결재자 우선, 서명자로 보강 (사다리: member → label / signerName → member)
    const baseNameFromLine = line.member?.name || line.label || '';
    const baseNameFromSig = sig?.signerName || sig?.member?.name || '';
    let baseName = assigneeUnset ? '' : baseNameFromLine || baseNameFromSig;

    // 탈퇴 표시: 서명에 연결된 member 기준
    const isDeleted = !!(sig?.member?.user?.deletedAt);

    let signatureData = sig?.signatureData || undefined;
    let stepStatus: MergedApprovalItem['status'] = sig
      ? sig.status === 'APPROVED'
        ? 'approved'
        : sig.status === 'REJECTED'
          ? 'rejected'
          : 'pending'
      : 'pending';

    // 작성 완료 서명이 채우는 칸(author + 위임 담당): 제출 전(round 서명 없음)에는 그 서명
    // (authorSignatureData)을 매핑한다. 제출 후에는 위 sig 오버레이가 그대로 표시된다.
    //
    // 이름의 정본은 **`authorSignerName`**이다 — 위임 담당 칸은 `line.member?.name`에도 담당자
    // 이름이 있지만, 제출 후 서버가 서명 행에 채우는 값이 `authorSignerName`이라 그쪽을 따라야
    // 제출 전후 표시가 갈라지지 않는다(담당자가 교체되면 line은 새 담당자를, 서명은 실제로
    // 작성한 사람을 가리키는데, 그 서명의 주인을 보여 주는 것이 옳다).
    if (authoringSlot && !sig) {
      if (authorSignerName) baseName = authorSignerName;
      if (authorSignatureData) {
        signatureData = authorSignatureData;
        stepStatus = 'approved';
      }
    }

    const name = isDeleted ? `${baseName} (탈퇴)` : baseName;

    return {
      // 행 식별자: 서명 id가 있으면 그것, 없으면 level(정의 안에서 유일). 구 `approverProfileId`
      // 접두는 프로필 축과 함께 사라졌다(E4) — 렌더에서는 React key로만 쓰인다.
      key: sig?.id || `level-${line.approvalLevel}`,
      approvalLevel: line.approvalLevel,
      label: stepLabel,
      name,
      signatureData,
      status: stepStatus,
      isAuthor,
      placeholder: line.placeholder,
    };
  });
}
